import React from "react";
import { observer } from "mobx-react-lite";
import { useLocalization } from "@progress/kendo-react-intl";
import { useEffect, useState, useRef } from "react";
import { useServices } from "src/services";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import {
  Grid,
  GridColumn,
  GridCellProps,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { useHistory } from "react-router";
import { D4URxEvent } from "src/utils/classes";
import { DataResult, process, State } from "@progress/kendo-data-query";
import { ColumnMenu } from "src/components/GridColumnMenus";
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import moment from 'moment';

interface Season {
  season_id: number;
  season_name: string;
  start_date: string;
  end_date: string;
}

interface ExportColumnDef {
  key: string;
  field: string;
  title: string;
}

const HistoryScreen = () => {
  const localizationService = useLocalization();
  const { d4ur } = useServices();
  const history = useHistory();

  // Excel export functionality
  const _export = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  // Refs for scroll synchronization
  const gridContentRef = useRef<HTMLDivElement | null>(null);
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);
  
  // State variables
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<Season | null>(null);
  const [events, setEvents] = useState<D4URxEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [eventsLoading, setEventsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(3000);
  const exportColumnDefs: ExportColumnDef[] = [
    { key: 'year', field: 'year', title: 'Year' },
    { key: 'event_id', field: 'event_id', title: 'Event ID' },
    { key: 'event_date', field: 'event_date', title: 'Event Date' },
    { key: 'event_name', field: 'event_name', title: 'Event Name' },
    { key: 'event_location_type', field: 'event_location_type', title: 'Location Type' },
    { key: 'dealer_name', field: 'dealer_name', title: 'Dealer Name' },
    { key: 'sales_code', field: 'sales_code', title: 'Sales Code' },
    { key: 'dealer_address', field: 'dealer_address', title: 'Dealer Address' },
    { key: 'dealer_city', field: 'dealer_city', title: 'Dealer City' },
    { key: 'dealer_state', field: 'dealer_state', title: 'Dealer State' },
    { key: 'dealer_zip', field: 'dealer_zip', title: 'Dealer Zip' },
    { key: 'organization_address', field: 'organization_address', title: 'Organization Address' },
    { key: 'organization_city', field: 'organization_city', title: 'Organization City' },
    { key: 'organization_state', field: 'organization_state', title: 'Organization State' },
    { key: 'organization_zip', field: 'organization_zip', title: 'Organization Zip' },
    { key: 'primary_contact', field: 'primary_contact', title: 'Primary Contact' },
    { key: 'charity_partner', field: 'charity_partner', title: 'Charity Partner' },
    { key: 'market_area', field: 'market_area', title: 'Market Area' },
    { key: 'region', field: 'region', title: 'Region' },
    { key: 'active', field: 'active', title: 'Active' },
    { key: 'status', field: 'status', title: 'Status' },
    { key: 'dealer_notes', field: 'dealer_notes', title: 'Dealer Notes' },
    { key: 'outreach_notes', field: 'outreach_notes', title: 'Outreach Notes' },
    { key: 'pre_registrant_count', field: 'pre_registrant_count', title: 'Pre-Event Registrations' },
    { key: 'pre_drives', field: 'pre_drives', title: 'Pre-Drives (Total)' },
    { key: 'post_drives', field: 'post_drives', title: 'Post-Drives' },
    { key: 'valid_post_drives', field: 'valid_post_drives', title: 'Valid Post-Drives' },
    { key: 'check_amount_pretty', field: 'check_amount_pretty', title: 'Check Amount' },
    { key: 'funding_purpose_category', field: 'funding_purpose_category', title: 'Funding Purpose Category' },
    { key: 'first_time_dealer', field: 'first_time_dealer', title: 'First Time Dealer' },
    { key: 'hispanic_dealer', field: 'hispanic_dealer', title: 'Hispanic Dealer' },
    { key: 'survey_count', field: 'survey_count', title: 'Registrants' },
    { key: 'funding_purpose', field: 'funding_purpose', title: 'Funding Purpose' },
  ];
  const [selectedExportColumns, setSelectedExportColumns] = useState<string[]>(
    exportColumnDefs.map((col) => col.key)
  );

  // Define dataState properly
  const [dataState] = useState<State>({
    sort: [
      {
        field: "start_date",
        dir: "desc",
      },
    ],
  });

  // Initialize dataResult with empty data
  const [dataResult, setDataResult] = useState<DataResult>({ 
    data: [], 
    total: 0 
  });

  // Fetch seasons on component mount
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        setLoading(true);
        const result = await d4ur.getSeasons();
        // console.log('Seasons API result:', result);
        
        if (result && (result as D4URxFetchResult).result) {
          const seasonData = (result as D4URxFetchResult).result as Season[];
          setSeasons(seasonData);
          if (seasonData.length > 0) {
            setSelectedSeason(seasonData[0]);
          }
        } else {
          setSeasons([]);
        }
      } catch (err) {
        // console.error('Error fetching seasons:', err);
        setError('Failed to fetch seasons data');
      } finally {
        setLoading(false);
      }
    };

    fetchSeasons();
  }, [d4ur]);

  // Fetch events when selected season changes
  useEffect(() => {
    const fetchEvents = async () => {
      if (!selectedSeason) {
        setEvents([]);
        setDataResult({ data: [], total: 0 });
        return;
      }

      try {
        setEventsLoading(true);
        setError(null);
        
        const result = await d4ur.getEventsBySeason(selectedSeason.season_id);
        console.log('Events for season:', result);
        
        if (result && (result as D4URxFetchResult).result) {
          const eventsData = (result as D4URxFetchResult).result;
          const processedEvents = Array.isArray(eventsData) 
            ? eventsData.map((e: any) => new D4URxEvent(e))
            : [new D4URxEvent(eventsData)];
          
          setEvents(processedEvents);
          setDataResult(process(processedEvents, dataState));
        } else {
          setEvents([]);
          setDataResult({ data: [], total: 0 });
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Failed to fetch events data');
        setEvents([]);
        setDataResult({ data: [], total: 0 });
      } finally {
        setEventsLoading(false);
      }
    };

    fetchEvents();
  }, [selectedSeason, d4ur, dataState]);

  // Measure grid scroll width and sync scroll positions
  useEffect(() => {
    if (events.length === 0) return;

    const measureWidth = () => {
      if (!gridContentRef.current) return;
      const gridContent = gridContentRef.current.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && gridContent.scrollWidth > 0) {
        setScrollbarWidth(gridContent.scrollWidth);
      }
    };

    const syncTopScrollbar = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && topScrollbarRef.current) {
        topScrollbarRef.current.scrollLeft = gridContent.scrollLeft;
      }
    };

    requestAnimationFrame(() => {
      measureWidth();
      setTimeout(measureWidth, 200);

      // Attach scroll listener to grid content after it renders
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) {
        gridContent.addEventListener('scroll', syncTopScrollbar);
      }
    });

    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.removeEventListener('scroll', syncTopScrollbar);
    };
  }, [events.length, dataResult.data.length]);

  const handleSeasonChange = (e: any) => {
    setSelectedSeason(e.target.value);
    // console.log('Selected season:', e.target.value);
  };

  const toggleExportColumn = (key: string) => {
    setSelectedExportColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const selectAllExportColumns = () => {
    setSelectedExportColumns(exportColumnDefs.map((col) => col.key));
  };

  const clearAllExportColumns = () => {
    setSelectedExportColumns([]);
  };

  const selectedExportColumnDefs = exportColumnDefs.filter((col) =>
    selectedExportColumns.includes(col.key)
  );

  const formatEventDateRange = (startDate?: any, endDate?: any): string => {
    if (!startDate && !endDate) return '';
    const start = startDate ? moment(startDate).format('M/D/YYYY') : '';
    const end = endDate ? moment(endDate).format('M/D/YYYY') : '';
    if (start && end) {
      return start === end ? start : `${start}-${end}`;
    }
    return start || end;
  };

  const formatNotesValue = (value: any): string => {
    if (!value) return '';

    const formatNoteObject = (noteObj: any): string => {
      if (!noteObj || typeof noteObj !== 'object') return '';
      const note = noteObj.note ? `${noteObj.note}`.trim() : '';
      const createdBy = noteObj.created_by_name || noteObj.created_by || '';
      const createdAt = noteObj.created_at || '';
      if (!note && !createdBy && !createdAt) return '';
      const metaParts = [createdBy, createdAt].filter(Boolean).join(' - ');
      if (!metaParts) return note;
      if (!note) return metaParts;
      return `${metaParts}: ${note}`;
    };

    const formatArray = (arr: any[]): string =>
      arr
        .map((item) => {
          if (!item) return '';
          if (typeof item === 'string') return item.trim();
          if (typeof item === 'object') return formatNoteObject(item);
          return `${item}`.trim();
        })
        .filter((entry) => entry && entry.trim() !== '')
        .join(' | ');

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return '';
      if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return formatArray(parsed);
          if (typeof parsed === 'object') return formatNoteObject(parsed);
          return `${parsed}`.trim();
        } catch {
          return trimmed;
        }
      }
      return trimmed;
    }

    if (Array.isArray(value)) return formatArray(value);
    if (typeof value === 'object') return formatNoteObject(value) || JSON.stringify(value);
    return `${value}`.trim();
  };

  // Format data for export
  const formatExportData = (data: any[]) => {
    return data.map((d: any) => {
      // Parse event_custom_data if it exists
      let eventLocationDetails = {};
      try {
        if (d.event_custom_data) {
          const customData = JSON.parse(d.event_custom_data);
          eventLocationDetails = {
            event_location_type: customData.event_location_type || '',
            kit_location: customData.kit_location || '',
            location_contact: customData.location_contact || '',
            location_phone: customData.location_phone || '',
            location_email: customData.location_email || '',
          };
        }
      } catch (e) {
        console.error('Error parsing event_custom_data:', e);
      }

      // Extract year from start_date the same way YearCell does
      const year = d.start_date ? new Date(d.start_date).getFullYear() : 'N/A';
      return {
        ...d,
        ...eventLocationDetails,
        // Add year field extracted from start_date
        year: year,
        // Format dates
        start_date: d.start_date ? moment(d.start_date).format('MM/DD/YYYY') : '',
        end_date: d.end_date ? moment(d.end_date).format('MM/DD/YYYY') : '',
        created_timestamp: d.created_timestamp ? moment(d.created_timestamp).format('MM/DD/YYYY HH:mm') : '',
        updated_timestamp: d.updated_timestamp ? moment(d.updated_timestamp).format('MM/DD/YYYY HH:mm') : '',
        training_completed: d.training_completed ? moment(d.training_completed).format('MM/DD/YYYY HH:mm') : '',
        event_date: formatEventDateRange(d.start_date, d.end_date),
        check_amount: d.check_amount ?? '',
        check_amount_pretty: d.check_amount ? `$${Number(d.check_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
        valid_post_drives: d.valid_post_drives ?? d.valid_post_count ?? 0,
        // Keep export fallbacks aligned with table rendering/model aliases.
        pre_registrant_count: d.pre_registrant_count ?? 0,
        pre_drives: d.pre_drives ?? d.total_pre_drives ?? d.survey_count ?? 0,
        post_drives: d.post_drives ?? d.total_post_drives ?? 0,
        first_time_dealer: d.first_time_dealer ? 'YES' : 'NO',
        hispanic_dealer: (() => {
          const src =
            d.hispanic !== undefined && d.hispanic !== null ? d.hispanic : d.hispanic_dealer;
          return src === true || src === 1 || src === '1' ? 'YES' : 'NO';
        })(),
        dealer_notes: formatNotesValue(d.dealer_notes),
        outreach_notes: formatNotesValue(d.outreach_notes_raw || d.outreach_notes),
        // Format boolean values
        bonus_event: d.bonus_event ? 'YES' : 'NO',
        cancelled: d.cancelled ? 'YES' : 'NO',
        ended: d.ended ? 'YES' : 'NO',
      };
    });
  };

  // Custom cell renderers
  const YearCell = (props: GridCellProps) => {
    const { start_date } = props.dataItem as any;
    const year = start_date ? new Date(start_date).getFullYear() : 'N/A';
    return <td>{year}</td>;
  };

  const EventDateRangeCell = (props: GridCellProps) => {
    const { start_date, end_date } = props.dataItem as any;
    const formattedDate = formatEventDateRange(start_date, end_date);
    return <td>{formattedDate || 'N/A'}</td>;
  };

  const EventNameCell = (props: GridCellProps) => {
    const { event_name, event_id } = props.dataItem as any;
    return (
      <td>
        <Button
          className="k-button k-button-link"
          onClick={() => {
            history.push(`/event/${event_id}`);
          }}
        >
          {event_name || 'N/A'}
        </Button>
      </td>
    );
  };

  const EventLocationTypeCell = (props: GridCellProps) => {
    const { event_custom_data } = props.dataItem as any;
    let locationType = 'N/A';
    
    try {
      if (event_custom_data) {
        const customData = JSON.parse(event_custom_data);
        locationType = customData.event_location_type || 'N/A';
      }
    } catch (e) {
      console.error('Error parsing event_custom_data:', e);
    }
    
    return <td>{locationType}</td>;
  };

  // Dealer address cell renderers
  const DealerAddressCell = (props: GridCellProps) => {
    const { dealer_address } = props.dataItem as any;
    return <td>{dealer_address || 'N/A'}</td>;
  };

  const DealerCityCell = (props: GridCellProps) => {
    const { dealer_city } = props.dataItem as any;
    return <td>{dealer_city || 'N/A'}</td>;
  };

  const DealerStateCell = (props: GridCellProps) => {
    const { dealer_state } = props.dataItem as any;
    return <td>{dealer_state || 'N/A'}</td>;
  };

  const DealerZipCell = (props: GridCellProps) => {
    const { dealer_zip } = props.dataItem as any;
    return <td>{dealer_zip || 'N/A'}</td>;
  };

  // Organization address cell renderers (these look for different fields)
  const OrgAddressCell = (props: GridCellProps) => {
    const { organization_address } = props.dataItem as any;
    return <td>{organization_address || 'N/A'}</td>;
  };

  const OrgCityCell = (props: GridCellProps) => {
    const { organization_city } = props.dataItem as any;
    return <td>{organization_city || 'N/A'}</td>;
  };

  const OrgStateCell = (props: GridCellProps) => {
    const { organization_state } = props.dataItem as any;
    return <td>{organization_state || 'N/A'}</td>;
  };

  const OrgZipCell = (props: GridCellProps) => {
    const { organization_zip } = props.dataItem as any;
    return <td>{organization_zip || 'N/A'}</td>;
  };

  const fundingPurposeCell = (props: GridCellProps) => {
    const { funding_purpose } = props.dataItem as any;
    return <td>{funding_purpose || 'N/A'}</td>;
  };

  const statusCell = (props: GridCellProps) => {
    const { status } = props.dataItem as any;
    return <td>{status || 'N/A'}</td>;
  };

  const dealerNotesCell = (props: GridCellProps) => {
    const { dealer_notes } = props.dataItem as any;
    return <td>{formatNotesValue(dealer_notes) || 'N/A'}</td>;
  };

  const outreachNotesCell = (props: GridCellProps) => {
    const { outreach_notes_raw, outreach_notes } = props.dataItem as any;
    const notes = formatNotesValue(outreach_notes_raw || outreach_notes);
    return <td>{notes || 'N/A'}</td>;
  };


  const numericCell = (field: string, fallbackField?: string) => (props: GridCellProps) => {
    const row = props.dataItem as any;
    const value = row[field] ?? (fallbackField ? row[fallbackField] : undefined);
    return <td>{value ?? 0}</td>;
  };

  const checkAmountCell = (props: GridCellProps) => {
    const { check_amount_pretty, check_amount } = props.dataItem as any;
    if (check_amount_pretty) return <td>{check_amount_pretty}</td>;
    if (check_amount === null || check_amount === undefined || check_amount === '') return <td>N/A</td>;
    return <td>{`$${Number(check_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</td>;
  };

  const yesNoCell = (field: string, fallbackField?: string) => (props: GridCellProps) => {
    const row = props.dataItem as any;
    const value = row[field] ?? (fallbackField ? row[fallbackField] : undefined);
    const isTruthy = value === true || value === 1 || value === '1';
    const isFalsy = value === false || value === 0 || value === '0' || value === null || value === undefined;
    if (isFalsy) return <td>NO</td>;
    if (isTruthy) return <td>YES</td>;
    return <td>{value ? 'YES' : 'NO'}</td>;
  };
  // console.log('dataResult', dataResult);
  return (
    <div id="History" className="dashboard-page main-content">
      <div className="card-container grid">
        <h3 className="card-title">
          {localizationService.toLanguageString("custom.history", "History")}
        </h3>
        
        {/* Season Selector Dropdown */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Select Season:
          </label>
          {!loading && seasons.length > 0 && (
            <DropDownList
              data={seasons}
              dataItemKey="season_id"
              textField="season_name"
              value={selectedSeason}
              onChange={handleSeasonChange}
              style={{ width: '200px' }}
            />
          )}
          {loading && <p>Loading seasons...</p>}
        </div>

        <div className="card-component">
          {error && <p style={{ color: 'red' }}>Error: {error}</p>}
          
          {/* Excel Export Component - Using type assertion to work around TypeScript compatibility issues */}
          {React.createElement(ExcelExport as any, {
            data: formatExportData(dataResult.data),
            ref: _export,
            fileName: `history_export_${selectedSeason ? selectedSeason.season_name : 'all'}_${moment().format('YYYYMMDD-hhmma')}.xlsx`
          }, selectedExportColumnDefs.map((col) =>
            React.createElement(ExcelExportColumn as any, { key: col.key, field: col.field, title: col.title })
          ))}
          
          {!loading && !error && selectedSeason && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4>Events for {selectedSeason.season_name}</h4>
                <button 
                  className="k-button k-button-md k-rounded-md k-button-solid" 
                  onClick={excelExport}
                  disabled={!events.length || selectedExportColumnDefs.length === 0}
                  style={{ marginLeft: 'auto' }}
                >
                  Export to Excel ({events.length} events, {selectedExportColumnDefs.length} columns)
                </button>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <details>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                    Choose Excel columns ({selectedExportColumnDefs.length} selected)
                  </summary>
                  <div style={{ marginTop: '8px' }}>
                    <button
                      className="k-button k-button-md k-rounded-md"
                      onClick={selectAllExportColumns}
                      type="button"
                      style={{ marginRight: '8px' }}
                    >
                      Select All
                    </button>
                    <button
                      className="k-button k-button-md k-rounded-md"
                      onClick={clearAllExportColumns}
                      type="button"
                    >
                      Clear All
                    </button>
                  </div>
                  <div
                    style={{
                      marginTop: '10px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(220px, 1fr))',
                      gap: '6px 12px'
                    }}
                  >
                    {exportColumnDefs.map((col) => (
                      <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="checkbox"
                          checked={selectedExportColumns.includes(col.key)}
                          onChange={() => toggleExportColumn(col.key)}
                        />
                        <span>{col.title}</span>
                      </label>
                    ))}
                  </div>
                </details>
              </div>
              
              {eventsLoading && <p>Loading events...</p>}
              
              {!eventsLoading && events.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <style>{`
                    /* Hide the grid's own horizontal scrollbar */
                    .history-grid-wrapper .k-grid-content {
                      overflow-x: scroll;
                    }
                    .history-grid-wrapper .k-grid-content::-webkit-scrollbar {
                      display: none;
                    }
                    .history-grid-wrapper .k-grid-content {
                      scrollbar-width: none;
                      -ms-overflow-style: none;
                    }
                    /* Sticky top scrollbar styling */
                    .history-top-scrollbar {
                      overflow-x: scroll;
                      overflow-y: hidden;
                      height: 14px;
                      position: sticky;
                      top: 0;
                      z-index: 50;
                      background: #f5f5f5;
                      border-bottom: 1px solid #ddd;
                    }
                    .history-top-scrollbar::-webkit-scrollbar {
                      height: 8px;
                    }
                    .history-top-scrollbar::-webkit-scrollbar-track {
                      background: #f1f1f1;
                    }
                    .history-top-scrollbar::-webkit-scrollbar-thumb {
                      background: #aaa;
                      border-radius: 4px;
                    }
                    .history-top-scrollbar::-webkit-scrollbar-thumb:hover {
                      background: #666;
                    }
                  `}</style>

                  {/* Outer scrollable viewport */}
                  <div style={{ height: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>

                    {/* Sticky top scrollbar */}
                    <div
                      ref={topScrollbarRef}
                      className="history-top-scrollbar"
                      onScroll={(e) => {
                        const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
                        if (gridContent) gridContent.scrollLeft = e.currentTarget.scrollLeft;
                      }}
                    >
                      <div style={{ width: scrollbarWidth, height: 1 }} />
                    </div>

                    {/* Grid */}
                    <div
                      ref={gridContentRef}
                      className="history-grid-wrapper"
                    >
                      <Grid
                        data={dataResult}
                        sortable={{ mode: "multiple" }}
                        resizable={true}
                        onColumnResize={() => {
                          const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
                          if (gridContent) setScrollbarWidth(gridContent.scrollWidth);
                        }}
                      >
                  <GridColumn
                    field="year"
                    title="Year"
                    cell={YearCell}
                    width={80}
                  />
                  <GridColumn
                    field="event_id"
                    title="Event ID"
                    columnMenu={ColumnMenu}
                    width={100}
                  />
                  <GridColumn
                    field="event_date"
                    title="Event Date"
                    cell={EventDateRangeCell}
                    width={180}
                  />
                  <GridColumn
                    field="event_name"
                    title="Event Name"
                    cell={EventNameCell}
                    width={180}
                  />
                  <GridColumn
                    field="event_location_type"
                    title="Location Type"
                    cell={EventLocationTypeCell}
                    width={140}
                  />
                  <GridColumn
                    field="dealer_name"
                    title="Dealer Name"
                    columnMenu={ColumnMenu}
                    width={180}
                  />
                  <GridColumn
                    field="sales_code"
                    title="Sales Code"
                    columnMenu={ColumnMenu}
                    width={120}
                  />
                  {/* <GridColumn
                    field="dealer_code"
                    title="Dealer Code"
                    columnMenu={ColumnMenu}
                    width={100}
                  /> */}
                  <GridColumn
                    field="dealer_address"
                    title="Dealer Address"
                    cell={DealerAddressCell}
                    width={180}
                  />
                  <GridColumn
                    field="dealer_city"
                    title="Dealer City"
                    cell={DealerCityCell}
                    width={120}
                  />
                  <GridColumn
                    field="dealer_state"
                    title="Dealer State"
                    cell={DealerStateCell}
                    width={120}
                  />
                  <GridColumn
                    field="dealer_zip"
                    title="Dealer Zip"
                    cell={DealerZipCell}
                    width={110}
                  />
                  <GridColumn
                    field="organization_address"
                    title="Organization Address"
                    cell={OrgAddressCell}
                    width={200}
                  />
                  <GridColumn
                    field="organization_city"
                    title="Organization City"
                    cell={OrgCityCell}
                    width={160}
                  />
                  <GridColumn
                    field="organization_state"
                    title="Organization State"
                    cell={OrgStateCell}
                    width={160}
                  />
                  <GridColumn
                    field="organization_zip_code"
                    title="Organization Zip"
                    cell={OrgZipCell}
                    width={150}
                  />
                  <GridColumn
                    field="primary_contact"
                    title="Primary Contact"
                    columnMenu={ColumnMenu}
                    width={160}
                  />
                  <GridColumn
                    field="charity_partner"
                    title="Charity Partner"
                    columnMenu={ColumnMenu}
                    width={170}
                  />
                  <GridColumn
                    field="market_area"
                    title="Market Area"
                    columnMenu={ColumnMenu}
                    width={130}
                  />
                  <GridColumn
                    field="region"
                    title="Region"
                    columnMenu={ColumnMenu}
                    width={100}
                  />
                  <GridColumn
                    field="active"
                    title="Active"
                    columnMenu={ColumnMenu}
                    width={100}
                  />
                  <GridColumn
                    field="status"
                    title="Status"
                    cell={statusCell}
                    width={100}
                  />
                  <GridColumn
                    field="dealer_notes"
                    title="Dealer Notes"
                    cell={dealerNotesCell}
                    width={220}
                  />
                  <GridColumn
                    field="outreach_notes"
                    title="Outreach Notes"
                    cell={outreachNotesCell}
                    width={260}
                  />
                  <GridColumn
                    field="pre_registrant_count"
                    title="Pre-Event Registrations"
                    cell={numericCell('pre_registrant_count')}
                    width={150}
                  />
                  <GridColumn
                    field="pre_drives"
                    title="Pre-Drives (Total)"
                    cell={numericCell('pre_drives', 'total_pre_drives')}
                    width={120}
                  />
                  <GridColumn
                    field="post_drives"
                    title="Post-Drives"
                    cell={numericCell('post_drives', 'total_post_drives')}
                    width={120}
                  />
                  <GridColumn
                    field="valid_post_drives"
                    title="Valid Post-Drives"
                    cell={numericCell('valid_post_drives', 'valid_post_count')}
                    width={150}
                  />
                  <GridColumn
                    field="check_amount"
                    title="Check Amount"
                    cell={checkAmountCell}
                    width={130}
                  />
                  <GridColumn
                    field="funding_purpose_category"
                    title="Funding Purpose Category"
                    columnMenu={ColumnMenu}
                    width={220}
                  />
                  <GridColumn
                    field="first_time_dealer"
                    title="First Time Dealer"
                    cell={yesNoCell('first_time_dealer')}
                    width={150}
                  />
                  <GridColumn
                    field="hispanic_dealer"
                    title="Hispanic Dealer"
                    cell={yesNoCell('hispanic_dealer', 'hispanic')}
                    width={140}
                  />
                  <GridColumn
                    field="survey_count"
                    title="Registrants"
                    columnMenu={ColumnMenu}
                    width={130}
                  />
                  <GridColumn
                    field="funding_purpose"
                    title="Funding Purpose"
                    cell={fundingPurposeCell}
                    width={400}
                  />
                      </Grid>
                    </div>
                  </div>
                </div>
              )}
              
              {!eventsLoading && events.length === 0 && (
                <p>No events found for {selectedSeason.season_name}</p>
              )}
              
              <p style={{ marginTop: '10px', color: '#666' }}>
                Total events: {events.length}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(HistoryScreen); 